import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { STATS, CONFIG } from "@/lib/data";

const LINES = [
  [{ t: "We Build What", grad: false }],
  [{ t: "Others Can't", grad: false }],
  [{ t: "Imagine.", grad: true }],
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TAGS = ["Performance", "Branding", "CGI", "Animation", "Websites", "Photography", "Motion"];

export const Hero = ({ onBook, onShowreel }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] w-full overflow-hidden">
      {/* Signature glow — bright orb concentrated on the right */}
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0">
        {CONFIG.showreelVideo && (
          <video
            className="absolute inset-0 h-full w-full object-cover opacity-40"
            poster={CONFIG.heroPoster}
            autoPlay muted loop playsInline
          >
            <source src={CONFIG.showreelWebm} type="video/webm" />
            <source src={CONFIG.showreelVideo} type="video/mp4" />
          </video>
        )}
        <div className="absolute top-1/2 right-[-4%] -translate-y-1/2 h-[85vh] w-[52vw] max-w-[860px] rounded-full blur-[80px] opacity-80"
             style={{ background: "radial-gradient(circle at 42% 50%, var(--accent-primary) 0%, transparent 60%)" }} />
        <div className="absolute top-[40%] right-[6%] -translate-y-1/2 h-[62vh] w-[42vw] max-w-[680px] rounded-full blur-[90px] opacity-75 animate-floaty"
             style={{ background: "radial-gradient(circle, var(--accent-secondary) 0%, transparent 62%)" }} />
        <div className="absolute top-[62%] right-[22%] h-[38vh] w-[30vw] rounded-full blur-[100px] opacity-55 animate-floaty"
             style={{ background: "var(--accent-gradient)", animationDelay: "-3s" }} />
        <div className="absolute top-[8%] left-[-10%] h-[52vh] w-[36vw] rounded-full blur-[120px] opacity-25"
             style={{ background: "radial-gradient(circle, var(--accent-primary) 0%, transparent 65%)" }} />
        {/* keep left readable */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--bg-primary) 12%, transparent 62%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: "linear-gradient(0deg, var(--bg-primary), transparent)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-6 sm:px-10 pt-28">
        {/* running tagline marquee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="relative mb-9 max-w-xl overflow-hidden marquee-pause"
        >
          <div className="flex w-max animate-marquee">
            {[...TAGS, ...TAGS].map((t, i) => (
              <span key={i} className="flex items-center font-heading text-xs sm:text-sm font-medium uppercase tracking-[0.34em] text-[var(--text-secondary)] whitespace-nowrap">
                <span className="px-5">{t}</span>
                <span className="text-[var(--accent-primary)]">✦</span>
              </span>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[var(--bg-primary)] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[var(--bg-primary)] to-transparent" />
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.92] text-[13vw] sm:text-[10vw] lg:text-[7rem]">
          {LINES.map((parts, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                {parts.map((p, j) => (
                  <span key={j} className={p.grad ? "text-gradient" : ""}>{p.t}</span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 max-w-lg font-body text-base sm:text-lg text-[var(--text-secondary)] leading-relaxed"
        >
          Premium websites, cinematic & CGI ads, 3D walkthroughs, branding, performance
          marketing, automation and custom AI agents — engineered under one roof.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button data-testid="hero-primary-cta" onClick={onBook}
                  className="btn-accent animate-pulse-glow rounded-full px-8 py-4 font-heading text-sm font-semibold">
            Let's Build Something Great
          </button>
          <button data-testid="hero-showreel-btn" onClick={onShowreel}
                  className="group inline-flex items-center gap-3 rounded-full border border-[var(--border-subtle)] px-6 py-4 font-heading text-sm font-medium hover:border-[var(--border-hover)] transition-colors">
            <span className="grid h-8 w-8 place-items-center rounded-full btn-accent">
              <Play size={13} className="ml-0.5" fill="currentColor" />
            </span>
            Watch Showreel
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-secondary)]/80 backdrop-blur-xl px-6 py-6"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl sm:text-4xl font-bold text-gradient">{s.value}</div>
              <div className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-[0.14em] text-[var(--text-muted)]">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
